/**
 * @class
 * @function MegaMenuLazy
 * Lazy-loads mega menu content on first hover/focus.
 * Parses JSON data and builds HTML client-side to avoid expensive Liquid rendering.
 */
if (!customElements.get('mega-menu-lazy')) {
  class MegaMenuLazy extends HTMLElement {
    constructor() {
      super();
      this._rendered = false;
    }

    connectedCallback() {
      const position = this.getAttribute('data-position');
      const globalScript = document.getElementById('mega-menu-data');
      if (!globalScript || !position) return;

      try {
        const allData = JSON.parse(globalScript.textContent);
        this._data = allData[position];
      } catch (e) {
        return;
      }
      if (!this._data) return;

      this._type = this._data.type || this.getAttribute('data-type');
      this._parentLi = this.closest('li');
      if (!this._parentLi) return;

      this._parentLi.addEventListener('mouseenter', () => this._render(), { once: true });
      this._parentLi.addEventListener('focusin', () => this._render(), { once: true });
    }

    _render() {
      if (this._rendered) return;
      this._rendered = true;

      const container = this.closest('.mega-menu-container');
      if (!container) return;

      if (this._type === 'sidebar') {
        this._renderSidebar(container);
      } else if (this._type === 'promotions') {
        this._renderPromotions(container);
      }
    }

    _esc(str) {
      if (!str) return '';
      const el = document.createElement('span');
      el.textContent = str;
      return el.innerHTML;
    }

    _renderImg(src, alt, width, height, cls) {
      if (!src) return '';
      const escapedAlt = this._esc(alt);
      const classAttr = cls ? ` class="lazyload ${cls}"` : ' class="lazyload"';
      return `<img${classAttr} width="${width || ''}" height="${height || ''}" data-sizes="auto" src="${src}" data-srcset="${src}" fetchpriority="low" alt="${escapedAlt}" />`;
    }

    _svgDropdownRight() {
      return '<svg width="5" height="8" viewBox="0 0 5 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.25 1L4.25 4L1.25 7" stroke="var(--color-header-links, --color-accent)" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    }

    _renderSidebar(container) {
      const d = this._data;
      const settings = window.megaMenuSettings || {};
      const t = window.megaMenuTranslations || {};
      const productAspectRatio = settings.product_aspect_ratio || 'square';
      const productsHoverImages = settings.products_hover_images || false;
      const productContentAlignment = settings.product_content_alignment || 'left';
      const productPricePosition = settings.product_price_position || 'after-product-title';
      const enableQuickView = settings.enable_quick_view || false;
      const saleBadgeType = settings.sale_badge_type || 'sale';
      const badgePosition = settings.badge_position || 'top-left';

      let ratio = 1;
      let sizes = '298x298';
      if (productAspectRatio === 'adapt') {
        sizes = '298x0';
      } else if (productAspectRatio === 'portrait') {
        ratio = 1 / 1.25;
        sizes = '298x350';
      } else if (productAspectRatio === 'landscape') {
        ratio = 1 / 0.75;
        sizes = '298x250';
      }

      // Build sidebar buttons
      let buttonsHtml = '';
      if (d.categories.length > 0) {
        buttonsHtml = '<ul>';
        d.categories.forEach((cat) => {
          const activeClass = cat.isFirst ? ' active' : '';
          const arrow = cat.hasChildren > 0 ? this._svgDropdownRight() : '';
          buttonsHtml += `<li role="none"><a href="${this._esc(cat.url)}" class="mega-menu-sidebar--button${activeClass}" data-target="#${this._esc(d.blockId)}-${cat.index}"><span>${this._esc(cat.title)}</span>${arrow}</a></li>`;
        });
        buttonsHtml += '</ul>';
      }

      // Build content panels
      let contentHtml = '';
      if (d.categories.length > 0) {
        d.categories.forEach((cat) => {
          const activeClass = cat.isFirst ? ' active' : '';
          let childrenHtml = '';
          cat.children.forEach((child) => {
            childrenHtml += '<li>';
            if (child.type === 'collection_link') {
              if (d.contentCollectionImage && child.imageUrl) {
                childrenHtml += `<a href="${this._esc(child.url)}" class="mega-menu-sidebar--content-container genelmega"><div class="mega-menu-sidebar--content-image">${this._renderImg(child.imageUrl, child.imageAlt, child.imageWidth, child.imageHeight)}</div><span class="text-button button">${this._esc(child.title)}</span></a>`;
              } else {
                childrenHtml += `<a href="${this._esc(child.url)}" class="mega-menu-sidebar--content-container genelmega"><span class="text-button button">${this._esc(child.title)}</span></a>`;
              }
            } else if (child.type === 'product_link' && child.product) {
              childrenHtml += this._renderProductCard(child.product, settings, t);
            } else {
              childrenHtml += `<a href="${this._esc(child.url)}" class="mega-menu-sidebar--content-container genelmega"><span class="text-button ">${this._esc(child.title)}</span></a>`;
            }
            childrenHtml += '</li>';
          });
          contentHtml += `<ul id="#${this._esc(d.blockId)}-${cat.index}" class="mega-menu-sidebar--content-inner${activeClass}">${childrenHtml}</ul>`;
        });
      }

      const html = `<div class="mega-menu-columns mega-menu-container--inner-has-sidebar"><mega-menu-sidebar class="mega-menu-sidebar"><div class="mega-menu-sidebar--sidebar">${buttonsHtml}</div><div class="mega-menu-sidebar--content">${contentHtml}</div></mega-menu-sidebar></div>`;

      container.innerHTML = html;

      // Load mega-menu-sidebar.js if the custom element isn't defined yet
      if (!customElements.get('mega-menu-sidebar')) {
        const scriptUrl = container.closest('full-menu')?.querySelector('script[src*="mega-menu-sidebar"]')?.src;
        if (scriptUrl) {
          const s = document.createElement('script');
          s.src = scriptUrl;
          s.defer = true;
          container.appendChild(s);
        }
      } else {
        // Trigger connectedCallback by upgrading the element
        const el = container.querySelector('mega-menu-sidebar');
        if (el) {
          customElements.upgrade(el);
        }
      }

      // Unveil lazyload images
      this._unveilImages(container);
    }

    _renderProductCard(p, settings, t) {
      const productAspectRatio = settings.product_aspect_ratio || 'square';
      const productsHoverImages = settings.products_hover_images || false;
      const productContentAlignment = settings.product_content_alignment || 'left';
      const productPricePosition = settings.product_price_position || 'after-product-title';
      const enableQuickView = settings.enable_quick_view || false;
      const badgePosition = settings.badge_position || 'top-left';
      const saleBadgeType = settings.sale_badge_type || 'sale';

      let ratio = 1;
      if (productAspectRatio === 'adapt' && p.featuredImageAspectRatio) {
        ratio = p.featuredImageAspectRatio;
      } else if (productAspectRatio === 'portrait') {
        ratio = 1 / 1.25;
      } else if (productAspectRatio === 'landscape') {
        ratio = 1 / 0.75;
      }
      const paddingBottom = (1 / ratio) * 100;

      const hoverClass = productsHoverImages && p.hasMultipleImages > 1 ? ' thb-hover' : '';

      // Badges
      let badgesHtml = `<span class="product-card--badges ${this._esc(badgePosition)}">`;
      if (!p.available) {
        badgesHtml += `<span class="badge out-of-stock">${this._esc(t.sold_out || 'Sold out')}</span>`;
      }
      if (p.onSale) {
        if (saleBadgeType === 'sale') {
          badgesHtml += `<span class="badge onsale">${this._esc(t.on_sale || 'Sale')}</span>`;
        } else if (p.saleBadge) {
          badgesHtml += `<span class="badge onsale">${this._esc(t.save_html ? t.save_html.replace('[saved_amount]', p.saleBadge) : ('Save ' + p.saleBadge))}</span>`;
        }
      }
      if (p.preOrder) {
        badgesHtml += `<span class="badge pre-order">${this._esc(t.pre_order || 'Pre-order')}</span>`;
      }
      badgesHtml += '</span>';

      // Images
      let secondaryImg = '';
      if (productsHoverImages && p.secondaryImage) {
        secondaryImg = this._renderImg(p.secondaryImage, p.secondaryImageAlt, 298, 298, 'product-secondary-image');
      }
      let primaryImg = '';
      if (p.featuredImage && p.showImage !== false) {
        primaryImg = this._renderImg(p.featuredImage, p.featuredImageAlt, p.featuredImageWidth, p.featuredImageHeight, 'product-primary-image');
      }
      if (!primaryImg) {
        primaryImg = '<div class="thb-placeholder"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5"></svg></div>';
      }

      // Quick view
      let quickViewHtml = '';
      if (enableQuickView) {
        quickViewHtml = `<quick-view class="product-card-quickview" data-product-handle="${this._esc(p.handle)}" tabindex="-1"><div class="loading-overlay"><svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="spinner-path" fill="none" stroke-width="6" cx="33" cy="33" r="30" stroke="var(--color-body)"></circle></svg></div><span>${this._esc(t.quick_view || 'Quick view')}</span></quick-view>`;
      }

      // Price
      const priceHtml = this._renderPrice(p, settings, t);

      const titleHtml = `<a href="${this._esc(p.url)}" title="${this._esc(p.title)}" class="product-card-title">${this._esc(p.title)}</a>`;

      let infoContent = '';
      if (productPricePosition === 'before-product-title') {
        infoContent = priceHtml + titleHtml;
      } else {
        infoContent = titleHtml + priceHtml;
      }

      return `<product-card class="product-card text-${this._esc(productContentAlignment)} product-card--content-spacing-false"><figure class="product-featured-image${hoverClass}">` +
        badgesHtml +
        `<a href="${this._esc(p.url)}" title="${this._esc(p.title)}" class="product-featured-image-link aspect-ratio aspect-ratio--${this._esc(productAspectRatio)}" style="--padding-bottom: ${paddingBottom}%;">` +
        secondaryImg + primaryImg + quickViewHtml +
        `</a></figure><div class="product-card-info">${infoContent}</div></product-card>`;
    }

    _renderPrice(p, settings, t) {
      const currencyCode = settings.currency_code_enabled || false;

      let compareHtml = '';
      if (p.compareAtPrice) {
        compareHtml = `<del><span class="amount">${p.compareAtPrice}</span></del>`;
      }

      const discountedClass = p.compareAtPrice ? ' discounted' : '';
      let moneyPrice = p.price || '';
      if (p.priceVaries && p.fromPrice) {
        moneyPrice = p.fromPrice;
      }

      let unitPriceHtml = '';
      if (p.unitPrice) {
        let refValue = '';
        if (p.unitPriceReferenceValue != null) {
          refValue = p.unitPriceReferenceValue;
        }
        unitPriceHtml = `<small class="unit-price"><span>${p.unitPrice}</span><span class="unit-price-separator">/</span><span>${refValue}${this._esc(p.unitPriceReferenceUnit || '')}</span></small>`;
      } else {
        unitPriceHtml = '<small class="unit-price hidden"></small>';
      }

      return `<span class="price">${compareHtml}<ins><span class="amount${discountedClass}">${moneyPrice}</span></ins>${unitPriceHtml}</span>`;
    }

    _renderPromotions(container) {
      const d = this._data;
      const limitedClass = d.limitColumns ? ' mega-menu-columns--limited' : '';

      let columnsHtml = '';
      d.columns.forEach((col) => {
        const activeClass = col.active ? ' active' : '';
        let childrenHtml = '';
        if (col.children.length > 0) {
          childrenHtml = '<ul role="menu">';
          col.children.forEach((sub) => {
            const subActive = sub.active ? ' active' : '';
            childrenHtml += `<li class="${subActive}" role="none"><a href="${this._esc(sub.url)}" class="thb-full-menu--link" role="menuitem">${this._esc(sub.title)}</a></li>`;
          });
          childrenHtml += '</ul>';
        }
        columnsHtml += `<li class="${activeClass}" role="none"><a href="${this._esc(col.url)}" class="thb-full-menu--link mega-menu-columns__heading" role="menuitem">${this._esc(col.title)}</a>${childrenHtml}</li>`;
      });

      // Promotions
      let promosHtml = '';
      const promos = (d.promotions || []).filter(Boolean);
      promos.forEach((promo) => {
        let contentInner = '';
        if (promo.heading) {
          contentInner += `<h6>${this._esc(promo.heading)}</h6>`;
        }
        if (promo.content) {
          contentInner += `<p>${this._esc(promo.content)}</p>`;
        }
        let linkHtml = '';
        if (promo.link) {
          linkHtml = `<a href="${this._esc(promo.link)}" class="mega-menu-promotion--link"></a>`;
        }
        promosHtml += `<li><div class="mega-menu-promotion"><div class="mega-menu-promotion--image">${this._renderImg(promo.imageUrl, promo.imageAlt, promo.imageWidth, promo.imageHeight)}</div><div class="mega-menu-promotion--content">${contentInner}</div>${linkHtml}</div></li>`;
      });

      container.innerHTML = `<ul class="mega-menu-columns${limitedClass}" tabindex="-1" style="--max-megamenu-columns: ${d.maxColumns}">${columnsHtml}${promosHtml}</ul>`;

      this._unveilImages(container);
    }

    _unveilImages(container) {
      if (typeof lazySizes !== 'undefined') {
        container.querySelectorAll('img.lazyload').forEach((img) => {
          lazySizes.loader.unveil(img);
        });
      }
    }
  }
  customElements.define('mega-menu-lazy', MegaMenuLazy);
}
